import { useState } from 'react';

import { type AxiosRequestConfig } from 'axios';

import { showToast } from '../components/atoms/Toast/Toast';

import { axiosInstance } from '../utils/axiosInstance';
import { triggerEvent } from '../utils';

import { type IPurchaseLeadExpressResponse } from '../pages/api/v1/purchase/lead-express';

interface IUseLeadExpressCheckoutParams {
  uuid: string;
  id: number | string;
  userToken?: string;
  /**
   * Sufixo aplicado aos steps do New Relic para diferenciar a origem da compra
   * (ex.: `_app` na pós request do app, `_order_details` no acompanhamento).
   */
  trackingSuffix?: string;
}

const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro ao tentar contratar o serviço';

export function useLeadExpressCheckout({
  uuid,
  id,
  userToken,
  trackingSuffix = '',
}: IUseLeadExpressCheckoutParams) {
  const [isPayingForServiceExpress, setIsPayingForServiceExpress] =
    useState(false);

  function addPageAction(step: string) {
    window.newrelic?.addPageAction('create_order_action', {
      step: `${step}${trackingSuffix}`,
    });
  }

  async function payForServiceExpress() {
    try {
      setIsPayingForServiceExpress(true);

      requestAnimationFrame(() => {
        setTimeout(() => {
          triggerEvent('open-express-service');
        }, 0);
      });

      const requestOptions: AxiosRequestConfig = {
        url: `${process.env.NEXT_PUBLIC_API_CLIENTS}/v1/purchase/lead-express`,
        method: 'POST',
        data: {
          requestUuid: uuid,
          requestId: id,
          userToken: userToken ? userToken : '',
        },
      };

      addPageAction('click_button_lead_express');

      const { data } =
        await axiosInstance<IPurchaseLeadExpressResponse>(requestOptions);

      if (data.status === 'error' && data.message) {
        showToast({ message: data.message, type: 'error' });

        setIsPayingForServiceExpress(false);

        return;
      }

      if (data.url) {
        addPageAction('click_button_lead_express_success');

        window.location.href = data.url;

        return;
      }

      showToast({ message: DEFAULT_ERROR_MESSAGE, type: 'error' });

      setIsPayingForServiceExpress(false);
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || DEFAULT_ERROR_MESSAGE,
        type: 'error',
      });

      addPageAction('click_button_lead_express_error');

      setIsPayingForServiceExpress(false);
    }
  }

  return {
    isPayingForServiceExpress,
    payForServiceExpress,
  };
}
